import { BreakpointObserver } from "@angular/cdk/layout";
import { computed, Injectable, inject, type Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { map, shareReplay } from "rxjs/operators";

export const TAILWIND_BREAKPOINTS = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
} as const;

export type BreakpointKey = keyof typeof TAILWIND_BREAKPOINTS;

export type BreakpointMap = {
  [K in keyof typeof TAILWIND_BREAKPOINTS]: boolean;
};

/**
 * Service to provide viewport breakpoint signals for responsive UI logic.
 *
 * @example
 * const viewport = inject(ViewportService);
 * if (viewport.isTablet()) { ... }
 */
@Injectable({ providedIn: "root" })
export class ViewportService {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly BREAKPOINT_QUERIES = Object.values(TAILWIND_BREAKPOINTS);
  private readonly ORDERED_BREAKPOINTS: readonly BreakpointKey[] = [
    "2xl",
    "xl",
    "lg",
    "md",
    "sm",
  ] as const;
  private readonly INITIAL_BREAKPOINT_STATE: BreakpointMap = {
    sm: false,
    md: false,
    lg: false,
    xl: false,
    "2xl": false,
  };

  /**
   * Signal containing the state of all breakpoints
   */
  private readonly breakpointState = toSignal(
    this.breakpointObserver.observe(this.BREAKPOINT_QUERIES).pipe(
      map((result): BreakpointMap => {
        const state = { ...this.INITIAL_BREAKPOINT_STATE };

        for (const [key, query] of Object.entries(TAILWIND_BREAKPOINTS)) {
          state[key as BreakpointKey] = result.breakpoints[query] ?? false;
        }

        return state;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    ),
    { initialValue: this.INITIAL_BREAKPOINT_STATE },
  );

  /**
   * Signal returning the active breakpoint (the largest that matches).
   * Returns 'sm' by default for screens < 640px.
   */
  readonly activeBreakpoint: Signal<BreakpointKey> = computed(() => {
    const state = this.breakpointState();
    return this.ORDERED_BREAKPOINTS.find((bp) => state[bp]) ?? "sm";
  });

  // Helpers
  readonly isMobile = computed(() => this.activeBreakpoint() === "sm");
  readonly isTablet = computed(
    () => this.activeBreakpoint() === "md" || this.activeBreakpoint() === "lg",
  );
  readonly isDesktop = computed(
    () => this.activeBreakpoint() === "xl" || this.activeBreakpoint() === "2xl",
  );
  readonly isTouch = computed(() => this.isMobile() || this.isTablet());
}
